import { TerminatedReason } from 'src/graphql/types/applicants'
import { formatSnakeValue } from 'src/lib/formatters'

export type TerminatedReasonValue =
  (typeof TerminatedReason)[keyof typeof TerminatedReason]

// Display-label overrides for TerminatedReason values whose humanized enum
// name isn't the copy we want to show. The enum VALUE is intentionally left
// unchanged — the database, analytics, exports, and migrations all still use
// VIOLATION_OF_COMPANY_POLICY; only the user-facing label differs. Reasons not
// listed here fall back to formatSnakeValue() (Title Case from SNAKE_CASE).
//
// Keyed by the TerminatedReason enum/constant (no string literals) so adding a
// new override stays type-safe against the schema.
export const TERMINATED_REASON_LABEL_OVERRIDES: Partial<
  Record<TerminatedReasonValue, string>
> = {
  [TerminatedReason.VIOLATION_OF_COMPANY_POLICY]: 'Company Policy',
}

/**
 * Title Case label for a TerminatedReason, honoring TERMINATED_REASON_LABEL_OVERRIDES.
 * Use this anywhere a terminatedReason is shown to a user (dropdowns, chips,
 * table cells) instead of calling formatSnakeValue() directly.
 */
export const formatTerminatedReason = (reason: string): string =>
  TERMINATED_REASON_LABEL_OVERRIDES[reason as TerminatedReasonValue] ??
  formatSnakeValue(reason)

export type TerminatedReasonOption = {
  value: TerminatedReasonValue
  label: string
}

// Sorted by the label the user reads, not by the enum value, so overridden
// labels (VIOLATION_OF_COMPANY_POLICY -> "Company Policy") land in the right
// place in every dropdown.
export const TERMINATED_REASON_OPTIONS: TerminatedReasonOption[] =
  Object.values(TerminatedReason)
    .map((reason) => ({
      value: reason,
      label: formatTerminatedReason(reason),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
